import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, SearchX, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PositionPicker } from "@/components/PositionPicker";

const CLAIM_CODES = ["GK", "DC", "DG", "DD", "MDC", "MC", "MOC", "AG", "AD", "BU"];
const YEAR = new Date().getFullYear();
const btn = "w-full rounded-2xl bg-cyan-400 text-zinc-950 font-display font-black py-4 active:scale-[0.98] transition disabled:opacity-40";
const ghost = "w-full rounded-2xl bg-white border border-slate-200 py-3.5 text-sm font-bold";

const Title = ({ children, sub }) => (
  <div className="mb-6">
    <h1 className="font-display text-3xl font-extrabold">{children}</h1>
    {sub && <p className="text-sm text-slate-500 mt-2">{sub}</p>}
  </div>
);

export default function Claim({ mode = "claim" }) {
  const nav = useNavigate();
  const { signIn } = useAuth();
  const [step, setStep] = useState("email");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ email: "", full_name: "", code: "", token: "", password: "", confirm: "", group_join_year: "", football_start_year: "", primary_position: "", secondary_position: null });
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const sendCode = () => run(async () => {
    await api.post("/claim/send-code", { email: f.email, purpose: mode });
    toast.success("Code envoyé !");
    setStep("code");
  });
  const lookup = () => run(async () => {
    if (mode === "reset") return sendCode();
    const r = await api.post("/claim/lookup", { email: f.email });
    if (r.data.status === "found") setF((x) => ({ ...x, full_name: r.data.full_name }));
    setStep(r.data.status);
  });
  const verify = () => run(async () => {
    const r = await api.post("/claim/verify", { email: f.email, code: f.code, purpose: mode });
    setF((x) => ({ ...x, token: r.data.token }));
    setStep("password");
  });
  const afterPassword = () => {
    if (mode === "claim") return setStep("join");
    run(async () => {
      const r = await api.post("/auth/reset-password", { token: f.token, password: f.password });
      signIn(r.data.token, r.data.user);
      toast.success("Mot de passe mis à jour");
      nav("/");
    });
  };
  const complete = (secondary) => run(async () => {
    const r = await api.post("/claim/complete", { token: f.token, password: f.password, group_join_year: +f.group_join_year,
      football_start_year: +f.football_start_year, primary_position: f.primary_position, secondary_position: secondary });
    setDone(r.data);
    setStep("ready");
  });

  const onboarding = ["join", "football", "primary", "secondary"];
  const oi = onboarding.indexOf(step);
  const yearOk = (y) => +y >= 1940 && +y <= YEAR;

  const screens = {
    email: (
      <>
        <Title sub={mode === "claim" ? "Entrez l'adresse email associée à votre profil joueur." : "Entrez l'adresse email de votre compte. Nous vous enverrons un code."}>
          {mode === "claim" ? "Réclamer mon compte" : "Mot de passe oublié"}
        </Title>
        <input data-testid="claim-email" type="email" autoComplete="email" placeholder="Adresse email" className="gg-input" value={f.email} onChange={set("email")} />
        <button data-testid="claim-search" disabled={!/\S+@\S+\.\S+/.test(f.email) || busy} onClick={lookup} className={`${btn} mt-4`}>
          {mode === "claim" ? "Rechercher mon compte" : "Recevoir un code"}
        </button>
      </>
    ),
    not_found: (
      <div className="text-center" data-testid="claim-not-found">
        <SearchX className="w-14 h-14 mx-auto text-orange-500" />
        <Title sub="Nous n'avons trouvé aucun profil joueur associé à cette adresse email.">Aucun compte trouvé</Title>
        <button data-testid="claim-retry" onClick={() => setStep("email")} className={btn}>Essayer une autre adresse</button>
      </div>
    ),
    claimed: (
      <div className="text-center space-y-3" data-testid="claim-already">
        <ShieldCheck className="w-14 h-14 mx-auto text-cyan-500" />
        <Title>Ce compte a déjà été activé.</Title>
        <Link to="/connexion" data-testid="claim-go-login" className={`${btn} block`}>Se connecter</Link>
        <Link to="/mot-de-passe-oublie" data-testid="claim-go-reset" className={`${ghost} block`}>Mot de passe oublié</Link>
      </div>
    ),
    found: (
      <div className="text-center" data-testid="claim-found">
        <p className="text-sm font-bold text-cyan-600">Nous avons trouvé votre profil</p>
        <p data-testid="claim-full-name" className="font-display text-3xl font-extrabold my-6">{f.full_name}</p>
        <p className="font-semibold mb-4">Est-ce bien vous ?</p>
        <button data-testid="claim-confirm" disabled={busy} onClick={sendCode} className={btn}>Oui, c'est moi</button>
        <button data-testid="claim-deny" onClick={() => setStep("email")} className={`${ghost} mt-2`}>Non, revenir</button>
      </div>
    ),
    code: (
      <>
        <Title sub="Nous avons envoyé un code de vérification à votre adresse email.">Vérifiez votre email</Title>
        <input data-testid="claim-code" inputMode="numeric" maxLength={6} placeholder="Code à 6 chiffres" className="gg-input num text-center text-2xl tracking-[0.5em]"
          value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.replace(/\D/g, "") })} />
        <button data-testid="claim-verify" disabled={f.code.length !== 6 || busy} onClick={verify} className={`${btn} mt-4`}>Vérifier</button>
        <button data-testid="claim-resend" disabled={busy} onClick={sendCode} className="w-full mt-3 text-sm text-cyan-600 font-semibold">Renvoyer le code</button>
      </>
    ),
    password: (
      <>
        <Title sub="8 caractères minimum.">{mode === "claim" ? "Créer votre mot de passe" : "Nouveau mot de passe"}</Title>
        <div className="space-y-3">
          <input data-testid="claim-password" type="password" autoComplete="new-password" placeholder="Mot de passe" className="gg-input" value={f.password} onChange={set("password")} />
          <input data-testid="claim-password-confirm" type="password" autoComplete="new-password" placeholder="Confirmer le mot de passe" className="gg-input" value={f.confirm} onChange={set("confirm")} />
          {f.confirm && f.confirm !== f.password && <p className="text-xs text-red-500">Les mots de passe ne correspondent pas.</p>}
        </div>
        <button data-testid="claim-password-continue" disabled={f.password.length < 8 || f.password !== f.confirm || busy} onClick={afterPassword} className={`${btn} mt-4`}>Continuer</button>
      </>
    ),
    join: (
      <>
        <Title sub="Sert à calculer votre ancienneté dans le groupe.">Depuis quelle année jouez-vous avec notre groupe ?</Title>
        <input data-testid="claim-join-year" type="number" inputMode="numeric" placeholder="Année de début (ex : 2022)" className="gg-input num" value={f.group_join_year} onChange={set("group_join_year")} />
        <button data-testid="claim-join-continue" disabled={!yearOk(f.group_join_year)} onClick={() => setStep("football")} className={`${btn} mt-4`}>Continuer</button>
      </>
    ),
    football: (
      <>
        <Title>Depuis quelle année jouez-vous au football ?</Title>
        <input data-testid="claim-football-year" type="number" inputMode="numeric" placeholder="Année de début (ex : 2005)" className="gg-input num" value={f.football_start_year} onChange={set("football_start_year")} />
        <button data-testid="claim-football-continue" disabled={!yearOk(f.football_start_year)} onClick={() => setStep("primary")} className={`${btn} mt-4`}>Continuer</button>
      </>
    ),
    primary: (
      <>
        <Title>Quel est votre poste principal ?</Title>
        <PositionPicker codes={CLAIM_CODES} value={f.primary_position} onChange={(v) => { setF({ ...f, primary_position: v }); setStep("secondary"); }} testid="claim-primary" />
      </>
    ),
    secondary: (
      <>
        <Title>Quel est votre poste secondaire ?</Title>
        <PositionPicker codes={CLAIM_CODES} exclude={f.primary_position} value={f.secondary_position} onChange={(v) => !busy && complete(v)} testid="claim-secondary" />
        <button data-testid="claim-no-secondary" disabled={busy} onClick={() => complete(null)} className={`${ghost} mt-3`}>Aucun poste secondaire</button>
      </>
    ),
    ready: (
      <div className="text-center pt-6" data-testid="claim-ready">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-cyan-400 to-lime-300 flex items-center justify-center shadow-lg">
          <Check className="w-10 h-10 text-white" strokeWidth={3} />
        </motion.div>
        <Title>Votre profil est prêt</Title>
        <button data-testid="claim-go-profile" onClick={() => { signIn(done.token, done.user); nav("/profil"); }} className={btn}>Accéder à mon profil</button>
      </div>
    ),
  };

  return (
    <div className="min-h-[100dvh] mx-auto max-w-md px-6 py-8 flex flex-col" data-testid="claim-page">
      <div className="flex items-center gap-3 mb-8">
        <Link to="/connexion" className="p-2 -ml-2" data-testid="claim-back"><ArrowLeft className="w-5 h-5" /></Link>
        <img src="/icon-192.png" alt="Gogoats" className="w-9 h-9 rounded-xl" />
        {oi >= 0 && (
          <div className="flex-1">
            <p className="text-[11px] text-slate-500 font-semibold" data-testid="claim-progress">Étape {oi + 1} sur 4</p>
            <div className="flex gap-1 mt-1">{onboarding.map((s, i) => <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= oi ? "bg-cyan-400" : "bg-slate-200"}`} />)}</div>
          </div>
        )}
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
          {screens[step]}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
